import { useContext, useEffect, Fragment, ReactNode, useRef } from "react";
import { observer } from "mobx-react";
import { SummaryStore, UserStore } from "../../index";
import { useHistory } from "react-router";
import { reaction } from "mobx";
import {
  faMagnifyingGlass,
  faKey,
  faChartLine,
  faMobileScreen,
  faToolbox,
  faUsers,
  faClipboard,
  faGlobe,
  faCog,
  faMessage,
  faVault,
  faHouseChimney,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { PageContainer, PageLoader } from "../ui/PageComponents";
import { faFilePdf } from "@fortawesome/free-regular-svg-icons";
import BreadCrumbs from "../BreadCrumbs";
import CasesInfoBox from "../HomeComponents/CasesInfoBox";
import NewIncidentInfoBox from "../HomeComponents/NewIncidentInfoBox";

export type SectionStatus = "OK" | "ALERT";
export type HomeItemProps = {
  title: string;
  subtitle: string;
  pageRoute: string;
  faIcon: IconProp;
  sectionComponent?: ReactNode;
  status?: SectionStatus;
};

const HOME_SECTIONS = [
  {
    isAdminRequired: false,
    sectionName: "Case Management",
    pages: [
      {
        title: "Cases Overview",
        description: "View and manage cases",
        icon: faClipboard,
        pageRoute: "cases",
      },
      {
        title: "Search For Users",
        description: "Find User cases by Name or Member Number",
        icon: faMagnifyingGlass,
        pageRoute: "search-user",
      },
    ],
  },
  {
    isAdminRequired: false,
    sectionName: "Analytics & Reports",
    pages: [
      {
        title: `Gaming Incident Register`,
        description: "Search and export historical records",
        icon: faVault,
        pageRoute: "gaming-incident-register",
      },
      {
        title: "Performance Metrics",
        description: "View and analyze performance metrics",
        icon: faChartLine,
        pageRoute: "analytics",
      },
      {
        title: "Report Generation",
        description: "Create and export reports",
        icon: faFilePdf,
        pageRoute: "reporting",
      },
    ],
  },
  {
    isAdminRequired: false,
    sectionName: "Venue Configuration",
    pages: [
      {
        title: "Sms Configuration",
        description: "Configure SMS settings",
        icon: faMessage,
        pageRoute: "sms-configuration",
      },
      {
        title: "Device Management",
        description: "Manage venue devices",
        icon: faMobileScreen,
        pageRoute: "venue-devices",
      },
      {
        title: "Settings",
        description: "Configure venue settings",
        icon: faToolbox,
        pageRoute: "settings",
      },
    ],
  },

  {
    isAdminRequired: true,
    sectionName: "Geofencing Safety Measures",
    pages: [
      {
        title: "Geofence Safety Setup",
        description: "Monitor safety Fences",
        icon: faGlobe,
        pageRoute: "admin/geo-safety",
      },
      {
        title: "Geofence Creator",
        description: "Create and Edit fences",
        icon: faCog,
        pageRoute: "admin/geo-creator",
      },
    ],
  },
  {
    isAdminRequired: true,
    sectionName: "PE Administration",
    pages: [
      {
        title: "User Admin",
        description: "Create & Manage Command Centre Users",
        icon: faUsers,
        pageRoute: "admin/command-centre-users",
      },
      {
        title: "Incident App Device Keys",
        description: "Create & Manage Device Keys for Incident App",
        icon: faKey,
        pageRoute: "admin/incident-app-device-keys",
      },
      {
        title: "Responder App Device Keys",
        description: "Create & Manage Device Keys for Responder App",
        icon: faKey,
        pageRoute: "admin/responder-app-device-keys",
      },
      {
        title: "Venues Setup & Status",
        description: "Manage Venues Setup & Status",
        icon: faHouseChimney,
        pageRoute: "admin/venue-setup-status",
      },
    ],
  },
];

// "Report Generation" (the `reporting` page) is gated to specific venues; only
// these venue IDs see the card on the home dashboard. Note: the `/reporting`
// route itself is NOT venue-gated, so any venue can still reach the page
// directly by URL — this list only controls the home dashboard tile.
const REPORT_GENERATION_VENUE_IDS = [36, 17, 77];

const HomePage = observer(() => {
  const userStore = useContext(UserStore);
  const { selectedVenue } = userStore;
  const summaryStore = useContext(SummaryStore);
  const history = useHistory();
  const pollingTimer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    reaction(
      () => [userStore.isLoggedIn, userStore.selectedVenue],
      (values) => {
        if (!userStore.isLoggedIn) {
          history.push("/login");
        }

        if (!userStore.selectedVenue) {
          history.push("/select-venue");
        } else {
          summaryStore.resetCaseSearch();
          summaryStore.startHomeDataPolling();
        }
      },
      {
        fireImmediately: true,
      },
    );

    return () => {
      summaryStore.stopHomeDataPolling();
      if (pollingTimer.current) clearInterval(pollingTimer.current);
    };
  }, [selectedVenue]);

  return (
    <PageContainer centerContent>
      <BreadCrumbs routes={[{ route: "/home", name: "Home" }]} />
      {!summaryStore.dataLoaded ? (
        <PageLoader />
      ) : (
        <div className="mt-8">
          <NewIncidentInfoBox />
          {HOME_SECTIONS.map(({ isAdminRequired, sectionName, pages }, i) => (
            <div key={`${i}-${sectionName}`} className="mt-4">
              {(!isAdminRequired || (isAdminRequired && userStore.isAdmin)) && (
                <Fragment>
                  <div className="text-2xl font-bold">{sectionName}</div>
                  <CasesInfoBox sectionName={sectionName} />
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {pages
                      .filter(
                        (page) =>
                          page.pageRoute !== "reporting" ||
                          REPORT_GENERATION_VENUE_IDS.includes(
                            selectedVenue?.venueId ?? -1
                          )
                      )
                      .map((props, index) => (
                        <HomePagesCard
                          {...props}
                          {...{
                            sectionName: sectionName,
                            index: index,
                            userStore,
                          }}
                        />
                      ))}
                  </div>
                </Fragment>
              )}
            </div>
          ))}
        </div>
      )}
    </PageContainer>
  );
});

const HomePagesCard = ({
  title,
  description,
  icon,
  pageRoute,
  disabled,
  sectionName,
  index,
  userStore,
}: any) => {
  const history = useHistory();
  return (
    <div
      key={sectionName + index}
      onClick={() => {
        if (disabled) return;

        history.push(`/${pageRoute}`);
      }}
      className="h-52 bg-gray-200 rounded-lg p-4 hover:bg-gray-50 hover:shadow-md transition-all cursor-pointer"
    >
      <div className="flex justify-center items-center mb-4 w-10 h-10 rounded-full bg-[#eb4c2d] lg:h-12 lg:w-12">
        <FontAwesomeIcon color={"WHITE"} icon={icon} className="w-6 h-6" />
      </div>
      <h3 className="mb-2 text-xl font-bold dark:text-white">
        {title === "Gaming Incident Register"
          ? `${userStore.selectedVenue?.displayName} ${title}`
          : title}
      </h3>
      <p className="text-gray-500 dark:text-gray-400">{description}</p>
      {disabled && (
        <p className="text-gray-900 font-bold mt-2">
          *This page is coming soon
        </p>
      )}
    </div>
  );
};

export default HomePage;
